import React, { useState, useEffect } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Text,
  Stack,
  Card,
  Badge,
  Avatar,
  SimpleGrid,
  TextInput,
  Select,
  ActionIcon,
  Menu,
  rem,
  Grid,
  Tabs,
  Alert,
  Modal,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconUsers,
  IconPhone,
  IconMail,
  IconDots,
  IconEye,
  IconEdit,
  IconClock,
  IconHeart,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconPlayerPlay,
  IconRefresh,
  IconChevronLeft,
  IconUserPlus,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  useTeacherClasses,
  useClassStudents,


} from '../../hooks/useTeacher';
import type { Student } from '../../types/api';
import { SmartLoader } from '../../components/common/LoadingStates';

const ClassRoster: React.FC = () => {
  const navigate = useNavigate();
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState<string>('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] = useDisclosure(false);


  const {
    data: classes,
    isLoading: classesLoading,
    error: classesError,
  } = useTeacherClasses();

  const {
    data: students,
    isLoading: studentsLoading,
    error: studentsError,
    refetch: refetchStudents,
  } = useClassStudents(selectedClassId);

  // Enhanced data fetching using real API
  // const {
  //   data: teacherDashboard,
  //   isLoading: dashboardLoading,
  // } = useTeacherDashboardFromUser();

  // const {
  //   data: enhancedRoster,
  //   isLoading: rosterLoading,
  //   error: rosterError,
  // } = useTeacherClassRoster(selectedClassId, {
  //   active_only: true,
  // });

  // Set initial class when classes are loaded
  useEffect(() => {
    if (classes && classes.length > 0 && !selectedClassId) {
      setSelectedClassId(classes[0].id);
    }
  }, [classes, selectedClassId]);

  const selectedClass = classes?.find(cls => cls.id === selectedClassId);

  const filteredStudents = students?.filter(student => {
    const matchesSearch =
      student.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.last_name.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesAttendance = true; // Attendance status not available in Student type

    return matchesSearch && matchesAttendance;
  }) || [];

  const handleStudentClick = (student: Student) => {
    setSelectedStudent(student);
    openDetailModal();
  };

  const handleRefresh = () => {
    refetchStudents();
    notifications.show({
      title: 'Refreshed',
      message: 'Student roster has been updated',
      color: 'green',
    });
  };

  const getAttendanceStats = () => {
    if (!students) return { present: 0, absent: 0, late: 0, total: 0 };

    return {
      present: 0, // Attendance data not available in Student type
      absent: 0,
      late: 0,
      total: students.length,
    };
  };

  const stats = getAttendanceStats();

  return (
    <Stack gap="lg">
      {/* Header */}
      <Card withBorder>
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Class Roster</Title>
              <Text size="sm" c="dimmed">
                Manage students and attendance
              </Text>
            </div>
          </Group>
          <Group>
            <ActionIcon variant="subtle" onClick={handleRefresh}>
              <IconRefresh size={18} />
            </ActionIcon>
            <Button
              leftSection={<IconUserPlus size={16} />}
              onClick={() => navigate('/admin/students/new')}
            >
              Add Student
            </Button>
          </Group>
        </Group>
      </Card>

      {/* Class Selector */}
      <SmartLoader isLoading={classesLoading} error={classesError as Error | null}>
        {classes && (
          <Card withBorder>
            <Group justify="space-between" mb="md">
              <Title order={4}>Select Class</Title>
              <Text size="sm" c="dimmed">{classes.length} classes assigned</Text>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
              {classes.map((cls) => (
                <Paper
                  key={cls.id}
                  withBorder
                  p="md"
                  radius="md"
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedClass?.id === cls.id ? 'var(--mantine-color-blue-0)' : undefined,
                    borderColor: selectedClass?.id === cls.id ? 'var(--mantine-color-blue-6)' : undefined,
                  }}
                  onClick={() => setSelectedClassId(cls.id)}
                >
                  <Group>
                    <ThemeIcon size="lg" variant="light" color="blue">
                      <IconUsers size={20} />
                    </ThemeIcon>
                    <div style={{ flex: 1 }}>
                      <Text fw={500}>{cls.name}</Text>
                      <Text size="sm" c="dimmed">
                        {cls.grade_level} • {cls.student_count} students
                      </Text>
                      <Text size="xs" c="dimmed">
                        {cls.room_number} • {cls.schedule?.start_time || 'N/A'} - {cls.schedule?.end_time || 'N/A'}
                      </Text>
                    </div>
                    {selectedClass?.id === cls.id && (
                      <ThemeIcon color="blue" variant="light">
                        <IconCheck size={16} />
                      </ThemeIcon>
                    )}
                  </Group>
                </Paper>
              ))}
            </SimpleGrid>
          </Card>
        )}
      </SmartLoader>

      {/* Attendance Summary */}
      {selectedClass && (
        <Card withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>Today's Attendance - {selectedClass.name}</Title>
            <Text size="sm" c="dimmed">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
            </Text>
          </Group>
          <SimpleGrid cols={4} spacing="md">
            <div style={{ textAlign: 'center' }}>
              <ThemeIcon size="xl" variant="light" color="green" mx="auto" mb="xs">
                <IconCheck size={24} />
              </ThemeIcon>
              <Text fw={700} size="xl">{stats.present}</Text>
              <Text size="xs" c="dimmed">Present</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <ThemeIcon size="xl" variant="light" color="red" mx="auto" mb="xs">
                <IconX size={24} />
              </ThemeIcon>
              <Text fw={700} size="xl">{stats.absent}</Text>
              <Text size="xs" c="dimmed">Absent</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <ThemeIcon size="xl" variant="light" color="yellow" mx="auto" mb="xs">
                <IconClock size={24} />
              </ThemeIcon>
              <Text fw={700} size="xl">{stats.late}</Text>
              <Text size="xs" c="dimmed">Late</Text>
            </div>
            <div style={{ textAlign: 'center' }}>
              <ThemeIcon size="xl" variant="light" color="blue" mx="auto" mb="xs">
                <IconUsers size={24} />
              </ThemeIcon>
              <Text fw={700} size="xl">{stats.total}</Text>
              <Text size="xs" c="dimmed">Total</Text>
            </div>
          </SimpleGrid>
        </Card>
      )}

      {/* Filters */}
      <Card withBorder>
        <Title order={4} mb="md">Filters</Title>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              placeholder="Search students..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Select
              placeholder="Attendance status"
              leftSection={<IconFilter size={16} />}
              data={[
                { value: '', label: 'All Students' },
                { value: 'present', label: 'Present' },
                { value: 'absent', label: 'Absent' },
                { value: 'late', label: 'Late' },
                { value: 'early_pickup', label: 'Early Pickup' },
              ]}
              value={attendanceFilter}
              onChange={(value) => setAttendanceFilter(value || '')}
              clearable
            />
          </Grid.Col>
        </Grid>
      </Card>

      {/* Student Roster */}
      <SmartLoader
        isLoading={studentsLoading}
        error={studentsError as Error | null}
        isEmpty={!filteredStudents.length}
        emptyState={
          <Alert color="blue" title="No students found">
            {searchQuery || attendanceFilter
              ? "No students match your current filters. Try adjusting your search criteria."
              : "No students are enrolled in this class yet."
            }
          </Alert>
        }
      >
        <Card withBorder>
          <Group justify="space-between" mb="md">
            <Title order={4}>Students</Title>
            <Text size="sm" c="dimmed">
              {filteredStudents.length} of {students?.length || 0} students
            </Text>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {filteredStudents.map((student) => (
              <Card key={student.id} withBorder radius="md" style={{ cursor: 'pointer' }}>
                <Group justify="space-between" mb="sm">
                  <Group>
                    <Avatar size="lg" radius="xl">
                      {student.first_name[0]}{student.last_name[0]}
                    </Avatar>
                    <div>
                      <Text fw={500}>{student.first_name} {student.last_name}</Text>
                      <Text size="sm" c="dimmed">{student.gender}</Text>
                    </div>
                  </Group>
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon variant="subtle">
                        <IconDots size={18} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEye style={{ width: rem(14), height: rem(14) }} />}
                        onClick={() => handleStudentClick(student)}
                      >
                        View Details
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                        onClick={() => navigate(`/admin/students/${student.id}/edit`)}
                      >
                        Edit Profile
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconPhone style={{ width: rem(14), height: rem(14) }} />}
                        onClick={() => {
                          if (student.emergency_contact?.phone) {
                            window.open(`tel:${student.emergency_contact.phone}`);
                          }
                        }}
                      >
                        Call Parent
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>

                <Group justify="space-between" mb="sm">
                  <Text size="sm" fw={500}>Attendance</Text>
                  <Badge
                    color={student.enrollment_status === 'enrolled' ? 'green' : 'gray'}
                  >
                    {student.enrollment_status}
                  </Badge>
                </Group>

                {/* Arrival and pickup times not available in Student type */}

                {student.medical_notes && (
                  <Alert color="orange" p="xs" mt="sm">
                    <Group gap="xs">
                      <IconAlertTriangle size={16} />
                      <Text size="xs">Medical Notes</Text>
                    </Group>
                  </Alert>
                )}

                {/* Attendance buttons removed - not supported by current API */}
              </Card>
            ))}
          </SimpleGrid>
        </Card>
      </SmartLoader>

      {/* Student Detail Modal */}
      <Modal
        opened={detailModalOpened}
        onClose={closeDetailModal}
        title="Student Details"
        size="lg"
      >
        {selectedStudent && (
          <Stack gap="md">
            <Group>
              <Avatar size="xl" radius="xl">
                {selectedStudent.first_name[0]}{selectedStudent.last_name[0]}
              </Avatar>
              <div>
                <Title order={3}>{selectedStudent.first_name} {selectedStudent.last_name}</Title>
                <Text c="dimmed">
                  {selectedStudent.date_of_birth
                    ? `Born ${new Date(selectedStudent.date_of_birth).toLocaleDateString()}`
                    : 'Birth date not available'}
                </Text>
                <Badge
                  color={selectedStudent.enrollment_status === 'enrolled' ? 'green' : 'gray'}
                  mt="xs"
                >
                  {selectedStudent.enrollment_status}
                </Badge>
              </div>
            </Group>

            <Divider />

            <Tabs defaultValue="contacts">
              <Tabs.List>
                <Tabs.Tab value="contacts" leftSection={<IconPhone size={16} />}>
                  Contacts
                </Tabs.Tab>
                <Tabs.Tab value="activities" leftSection={<IconPlayerPlay size={16} />}>
                  Recent Activities
                </Tabs.Tab>
                <Tabs.Tab value="medical" leftSection={<IconHeart size={16} />}>
                  Medical Info
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="contacts" pt="md">
                <Stack gap="md">
                  {selectedStudent.emergency_contact && (
                    <Card withBorder>
                      <Group justify="space-between">
                        <div>
                          <Text fw={500}>{selectedStudent.emergency_contact.name}</Text>
                          <Text size="sm" c="dimmed">{selectedStudent.emergency_contact.relationship}</Text>
                          <Group gap="xs" mt="xs">
                            <ActionIcon
                              variant="light"
                              size="sm"
                              onClick={() => window.open(`tel:${selectedStudent.emergency_contact?.phone}`)}
                            >
                              <IconPhone size={14} />
                            </ActionIcon>
                            <Text size="xs">{selectedStudent.emergency_contact?.phone}</Text>
                          </Group>
                          {selectedStudent.emergency_contact?.email && (
                            <Group gap="xs">
                              <ActionIcon
                                variant="light"
                                size="sm"
                                onClick={() => window.open(`mailto:${selectedStudent.emergency_contact?.email}`)}
                              >
                                <IconMail size={14} />
                              </ActionIcon>
                              <Text size="xs">{selectedStudent.emergency_contact.email}</Text>
                            </Group>
                          )}
                        </div>
                        <Badge color="blue" size="sm">Emergency</Badge>
                      </Group>
                    </Card>
                  )}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="activities" pt="md">
                <Text c="dimmed">Activity history not available in current view</Text>
              </Tabs.Panel>

              <Tabs.Panel value="medical" pt="md">
                {selectedStudent.medical_notes ? (
                  <Alert color="orange" title="Medical Notes">
                    {selectedStudent.medical_notes}
                  </Alert>
                ) : (
                  <Text c="dimmed">No medical notes on file</Text>
                )}
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};

export default ClassRoster;
